import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import OrderForm from './OrderForm';
import Timeline from './Timeline';
import ResultCard from './ResultCard';
import ErrorMessage from './ErrorMessage';
import RecoveryHistory from './RecoveryHistory';
import ProgressBar from './ProgressBar';
import TelegramAd from './TelegramAd';
import StatsWidget from './StatsWidget';
import GlobalNotifications from './GlobalNotifications';
import UserStatsModal from './UserStatsModal';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import Toast from './Toast';
import CountdownTimer from './CountdownTimer';
import PWAInstallPrompt from './PWAInstallPrompt';
import OnboardingTour from './OnboardingTour';
import QuickActionsMenu from './QuickActionsMenu';
import DragDropImport from './DragDropImport';
import BrowserCheckModal from './BrowserCheckModal';
import { checkOrder } from '../services/api';
import { 
  getRecoveryHistory, 
  addToRecoveryHistory, 
  clearRecoveryHistory,
  exportHistory
} from '../services/storage';
import useKeyboardShortcuts from '../hooks/useKeyboardShortcuts';
import useToast from '../hooks/useToast';

// Этапы процесса восстановления
const STEPS = [
  { label: 'Ввод заказа', description: 'Введите номер заказа' },
  { label: 'Проверка', description: 'Проверяем заказ в системе' },
  { label: 'Генерация', description: 'Генерируем новый ключ' },
  { label: 'Готово', description: 'Ключ готов к использованию' }
];

function HomePage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [lastRestorationTime, setLastRestorationTime] = useState(null);
  const [isFormBlocked, setIsFormBlocked] = useState(false);
  const [clientIP, setClientIP] = useState(null);
  const [timeUntilUnblock, setTimeUntilUnblock] = useState(0);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [showBrowserCheck, setShowBrowserCheck] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);
  const orderInputRef = useRef(null);
  const { toasts, hideToast, success, error: showError, info } = useToast();

  // Загружаем историю при монтировании
  useEffect(() => {
    setHistory(getRecoveryHistory());
    
    // Загружаем время последнего восстановления
    const lastTime = localStorage.getItem('lastRestorationTime');
    if (lastTime) {
      setLastRestorationTime(parseInt(lastTime));
    }

    // Проверяем, был ли уже использован IP
    const rateLimiter = require('../services/rateLimiter').default;
    const ip = rateLimiter.getClientID();
    setClientIP(ip); // Сохраняем IP для последующего использования
    
    const attemptsInfo = rateLimiter.getAttemptsInfo(ip);
    
    // Проверяем блокировку через cookies (дополнительная защита)
    const isBlockedByCookies = rateLimiter.isBlockedByCookies();
    
    if (isBlockedByCookies || (attemptsInfo && attemptsInfo.hasUsedOnce && attemptsInfo.usedAt)) {
      const now = Date.now();
      const blockDuration = 3 * 60 * 1000; // 3 минуты
      const isBlockExpired = attemptsInfo ? (now - attemptsInfo.usedAt) > blockDuration : false;
      
      if (!isBlockExpired || isBlockedByCookies) {
        setIsFormBlocked(true);
        
        // Устанавливаем начальное время до разблокировки
        let initialTimeLeft = 0;
        if (isBlockedByCookies) {
          const blockedUntil = require('js-cookie').get('blockedUntil');
          if (blockedUntil) {
            initialTimeLeft = Math.max(0, parseInt(blockedUntil) - now);
          }
        } else if (attemptsInfo && attemptsInfo.usedAt) {
          initialTimeLeft = Math.max(0, blockDuration - (now - attemptsInfo.usedAt));
        }
        setTimeUntilUnblock(initialTimeLeft);
      }
    }
  }, []);

  // Автоматическая разблокировка формы через 3 минуты
  useEffect(() => {
    if (!isFormBlocked || !clientIP) return;

    const interval = setInterval(() => {
      const rateLimiter = require('../services/rateLimiter').default;
      const attemptsInfo = rateLimiter.getAttemptsInfo(clientIP);
      const isBlockedByCookies = rateLimiter.isBlockedByCookies();
      
      // Вычисляем время до разблокировки
      let timeLeft = 0;
      const now = Date.now();
      
      if (isBlockedByCookies) {
        // Получаем время из cookies
        const blockedUntil = require('js-cookie').get('blockedUntil');
        if (blockedUntil) {
          timeLeft = Math.max(0, parseInt(blockedUntil) - now);
        }
      } else if (attemptsInfo && attemptsInfo.hasUsedOnce && attemptsInfo.usedAt) {
        // Вычисляем время на основе attemptsInfo
        const blockDuration = 3 * 60 * 1000; // 3 минуты
        timeLeft = Math.max(0, blockDuration - (now - attemptsInfo.usedAt));
      }
      
      setTimeUntilUnblock(timeLeft);
      
      // Если время истекло, разблокируем форму
      if (timeLeft <= 0) {
        setIsFormBlocked(false);
        setTimeUntilUnblock(0);
      }
    }, 1000); // Проверяем каждую секунду

    return () => clearInterval(interval);
  }, [isFormBlocked, clientIP]);

  // Проверка задержки между восстановлениями
  const canRestore = () => {
    if (!lastRestorationTime) return true;
    
    const now = Date.now();
    const timeDiff = now - lastRestorationTime;
    const thirtySeconds = 30 * 1000;
    
    return timeDiff >= thirtySeconds;
  };

  // Получение оставшегося времени до следующего восстановления
  const getTimeUntilNextRestore = () => {
    if (!lastRestorationTime) return 0;
    
    const now = Date.now();
    const timeDiff = now - lastRestorationTime;
    const thirtySeconds = 30 * 1000;
    
    return Math.max(0, thirtySeconds - timeDiff);
  };

  const handleOrderSubmit = async (orderNumber, email) => {
    // Проверяем задержку между восстановлениями
    if (!canRestore()) {
      const remainingTime = Math.ceil(getTimeUntilNextRestore() / 1000);
      setError(`Пожалуйста, подождите ${remainingTime} секунд перед следующим восстановлением.`);
      return;
    }

    // Сохраняем данные заказа и показываем проверку браузера
    setPendingOrder({ orderNumber, email });
    setShowBrowserCheck(true);
  };

  const handleBrowserCheckConfirm = async () => {
    setShowBrowserCheck(false);
    
    if (!pendingOrder) return;
    
    const { orderNumber, email } = pendingOrder;

    setLoading(true);
    setError(null);
    setResult(null);
    setCurrentStep(1); // Переход к этапу "Проверка"

    try {
      // Имитация этапов проверки
      await new Promise(resolve => setTimeout(resolve, 800));
      setCurrentStep(2); // Переход к этапу "Генерация"

      const response = await checkOrder(orderNumber, history, email);
      
      await new Promise(resolve => setTimeout(resolve, 600));
      setCurrentStep(3); // Переход к этапу "Готово"

      setResult(response);
      
      // Сохраняем время восстановления
      const now = Date.now();
      setLastRestorationTime(now);
      localStorage.setItem('lastRestorationTime', now.toString());
      
      // Блокируем форму после использования
      setIsFormBlocked(true);
      setTimeUntilUnblock(3 * 60 * 1000); // 3 минуты в миллисекундах
      
      // Всегда обновляем историю актуальной подпиской
      // (даже если заказ уже был восстановлен ранее - обновляем на новую подписку)
      console.log('[App] Обновление истории заказа:', {
        orderNumber: response.orderNumber,
        isAlreadyRestored: response.isAlreadyRestored,
        hasSubscription: !!response.subscription
      });
      const updatedHistory = addToRecoveryHistory(response);
      setHistory(updatedHistory);
      console.log('[App] История обновлена, записей в истории:', updatedHistory.length);
    } catch (err) {
      // Обрабатываем разные типы ошибок
      let errorMessage = 'Произошла ошибка при проверке заказа';
      
      if (err.message === 'ORDER_NOT_FOUND') {
        errorMessage = 'Заказ с указанным номером не найден. Проверьте правильность номера заказа или убедитесь, что заказ был оформлен на Яндекс Маркете.';
      } else if (err.message === 'INVALID_ORDER') {
        errorMessage = 'Указанный номер заказа недействителен. Пожалуйста, проверьте формат номера заказа.';
      } else if (err.message === 'SERVER_ERROR') {
        errorMessage = 'Сервер временно недоступен. Попробуйте позже или обратитесь в поддержку.';
      } else if (err.message === 'API_ERROR') {
        errorMessage = 'Ошибка при обращении к серверу. Попробуйте еще раз.';
      } else if (err.message === 'INVALID_RESPONSE') {
        errorMessage = 'Получен некорректный ответ от сервера. Попробуйте позже.';
      } else if (err.message === 'NO_SUBSCRIPTION') {
        errorMessage = 'Не удалось получить подписку для данного заказа. Обратитесь в поддержку.';
      } else if (err.message === 'INVALID_SUBSCRIPTION_URL') {
        errorMessage = 'Получена некорректная ссылка на подписку. Обратитесь в поддержку.';
      } else if (err.message === 'FUNCTION_NOT_FOUND') {
        errorMessage = 'Ошибка конфигурации сервера. Обратитесь в поддержку.';
      } else if (err.message === 'KEY_ALREADY_CHANGED') {
        errorMessage = 'Этот заказ уже был обработан ранее. Если вам нужна новая подписка, обратитесь в поддержку.';
      } else if (err.message === 'RATE_LIMIT_EXCEEDED') {
        errorMessage = 'Превышен лимит попыток. Пожалуйста, подождите 15 минут перед следующей попыткой.';
      } else if (err.message === 'IP_BLOCKED') {
        errorMessage = 'Ваш IP временно заблокирован из-за подозрительной активности. Попробуйте позже.';
      } else if (err.message === 'INVALID_ORDER_FORMAT') {
        errorMessage = 'Некорректный формат номера заказа. Номер должен содержать от 10 до 12 цифр.';
      } else if (err.message === 'UNKNOWN_ERROR') {
        errorMessage = 'Произошла неизвестная ошибка. Попробуйте позже или обратитесь в поддержку.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setResult(null); // Сбрасываем результат при ошибке
      setCurrentStep(0); // Возврат к началу при ошибке
      
      // Блокируем форму после любой попытки (успешной или неудачной)
      setIsFormBlocked(true);
      setTimeUntilUnblock(3 * 60 * 1000); // 3 минуты в миллисекундах
    } finally {
      setLoading(false);
      setPendingOrder(null);
    }
  };

  const handleBrowserCheckCancel = () => {
    setShowBrowserCheck(false);
    setPendingOrder(null);
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setCurrentStep(0);
    // НЕ сбрасываем isFormBlocked - форма остается заблокированной
  };

  const handleSelectFromHistory = (item) => {
    setResult(item);
    setCurrentStep(3);
    setError(null);
  };

  const handleClearHistory = () => {
    if (window.confirm('Вы уверены, что хотите очистить всю историю восстановлений?')) {
      clearRecoveryHistory();
      setHistory([]);
    }
  };

  // Настройка горячих клавиш
  useKeyboardShortcuts({
    'ctrl+k': () => {
      // Фокус на поле ввода заказа
      if (orderInputRef.current) {
        orderInputRef.current.focus();
      }
    },
    'ctrl+e': () => {
      // Экспортировать историю
      if (history.length > 0) {
        exportHistory();
      }
    },
    'ctrl+m': () => {
      // Открыть статистику
      setShowStatsModal(true);
    },
    'ctrl+/': () => {
      // Показать помощь по горячим клавишам
      setShowKeyboardHelp(true);
    },
    'escape': () => {
      // Закрыть модальные окна
      setShowStatsModal(false);
      setShowKeyboardHelp(false);
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header 
        onOpenStats={() => setShowStatsModal(true)}
        onOpenHelp={() => setShowKeyboardHelp(true)}
      />
      
      <main className="container mx-auto px-4 py-4 md:py-8">
        <div className="max-w-4xl mx-auto">
          {/* История восстановлений */}
          {history.length > 0 && currentStep === 0 && (
            <RecoveryHistory 
              history={history}
              onSelectOrder={handleSelectFromHistory}
              onClearHistory={handleClearHistory}
              onUpdateHistory={setHistory}
            />
          )}

          {/* Timeline - показываем только когда есть активный процесс */}
          {(currentStep > 0 || result || loading) && (
            <div className="card p-4 md:p-6 mb-4 md:mb-6 animate-fade-in-up">
              <Timeline currentStep={currentStep} steps={STEPS} />
            </div>
          )}

          {/* Форма ввода - показываем только на первом этапе, без результата и без ошибки */}
          {currentStep === 0 && !result && !loading && !error && (
            <div className="animate-fade-in-up">
              {/* Карточка с ссылкой на страницу стран VPN */}
              <Link
                to="/countries"
                className="block mb-4 md:mb-6 card p-3 md:p-4 hover:shadow-lg transition-all duration-300 border-2 border-primary-300 hover:border-primary-500 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 transform hover:scale-[1.01] relative overflow-hidden group"
              >
                {/* Декоративные элементы */}
                <div className="absolute top-0 right-0 w-16 md:w-24 h-16 md:h-24 bg-white opacity-10 rounded-full -mr-8 md:-mr-12 -mt-8 md:-mt-12 group-hover:scale-150 transition-transform duration-500"></div>
                
                <div className="relative z-10 flex items-center gap-2 md:gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white text-xl md:text-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      🗳️
                    </div>
                  </div>
                  <div className="flex-1 text-white min-w-0">
                    <div className="flex items-center gap-1.5 md:gap-2 mb-1 flex-wrap">
                      <h3 className="text-base md:text-lg lg:text-xl font-bold">
                        Голосуйте за VPN локации
                      </h3>
                      <span className="px-2 py-0.5 md:px-2.5 md:py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs md:text-sm font-semibold animate-pulse whitespace-nowrap">
                        НОВОЕ
                      </span>
                    </div>
                    <p className="text-blue-100 text-xs md:text-sm leading-tight">
                      Выберите страны для вашей подписки
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white group-hover:bg-white/30 transition-colors">
                      <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>

              <OrderForm 
                ref={orderInputRef}
                onSubmit={handleOrderSubmit} 
                loading={loading} 
                isFormBlocked={isFormBlocked} 
                timeUntilUnblock={timeUntilUnblock} 
              />
              
              {/* Реклама Telegram бота с промокодом ОСЕНЬ77 */}
              <TelegramAd />
              
              {/* Статистика сервиса */}
              <StatsWidget />
            </div>
          )}


          {/* Процесс проверки - показываем на этапах 1 и 2 */}
          {loading && (currentStep === 1 || currentStep === 2) && (
            <div className="card p-6 md:p-8 text-center animate-scale-in">
              <div className="flex flex-col items-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                </div>
                
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2 animate-fade-in-down">
                  {STEPS[currentStep].label}
                </h3>
                <p className="text-sm md:text-base text-gray-600 animate-fade-in-down">
                  {STEPS[currentStep].description}...
                </p>
                
                {/* Прогресс-бар для процесса */}
                <div className="w-full max-w-xs mt-6">
                  <ProgressBar currentStep={currentStep} totalSteps={STEPS.length} />
                </div>
              </div>
            </div>
          )}

          {/* Ошибка - заменяет форму ввода */}
          {error && currentStep === 0 && (
            <div className="animate-slide-in-left">
              <ErrorMessage message={error} onRetry={handleReset} />
            </div>
          )}
          

          {/* Результат - показываем на последнем этапе */}
          {result && currentStep === 3 && (
            <div className="animate-slide-in-right">
              <ResultCard 
                result={result} 
                onNewOrder={handleReset} 
                showToast={success}
              />
            </div>
          )}

          {/* Таймер обратного отсчета - показываем когда форма заблокирована */}
          {isFormBlocked && timeUntilUnblock > 0 && currentStep === 0 && !result && (
            <div className="mb-4 animate-fade-in">
              <CountdownTimer 
                endTime={Date.now() + timeUntilUnblock}
                onComplete={() => {
                  setIsFormBlocked(false);
                  setTimeUntilUnblock(0);
                  success('Форма разблокирована! Можно восстанавливать подписку.');
                }}
              />
            </div>
          )}
        </div>
      </main>
      
      {/* Глобальные уведомления */}
      <GlobalNotifications />

      {/* Модальные окна */}
      <UserStatsModal 
        isOpen={showStatsModal} 
        onClose={() => setShowStatsModal(false)} 
      />
      <KeyboardShortcutsModal 
        isOpen={showKeyboardHelp} 
        onClose={() => setShowKeyboardHelp(false)} 
      />

      {/* Toast уведомления */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </div>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Onboarding Tour */}
      <OnboardingTour />

      {/* Quick Actions Menu */}
      <QuickActionsMenu
        onOpenStats={() => setShowStatsModal(true)}
        onOpenHelp={() => setShowKeyboardHelp(true)}
        onExportHistory={() => {
          exportHistory();
          success('История экспортирована!');
        }}
        onNewRecovery={() => {
          handleReset();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      {/* Drag & Drop Import */}
      <DragDropImport
        onImport={(updatedHistory) => {
          setHistory(updatedHistory);
          success('История успешно импортирована!');
        }}
        onError={(errorMsg) => {
          showError(errorMsg);
        }}
      />

      {/* Browser Check Modal */}
      <BrowserCheckModal
        isOpen={showBrowserCheck}
        onConfirm={handleBrowserCheckConfirm}
        onCancel={handleBrowserCheckCancel}
        onForceClose={handleBrowserCheckCancel}
      />
    </div>
  );
}

export default HomePage;
